import {
  ComponentEmotionWheelLevels,
  EmotionWheelPage,
  EmotionWheelEntity,
  Enum_Completedtask_Type,
} from '@/graphql/generated/graphql';
import { completeTask } from '@/services/daily/complete-tasks.service';
import { getEmotionWheel, getPage } from '@/services/daily/emotion-wheel.service';
import { create } from 'zustand';

interface EmotionWheelStore {
  L1: EmotionWheelEntity | null;
  L2: ComponentEmotionWheelLevels | null;
  slices: {
    id: string;
    emotion: string | undefined;
    start: string;
    end: string;
  }[];
  emotions: EmotionWheelEntity[];
  emotionWheelpage: EmotionWheelPage | null;
  loading: boolean;
  onInit: () => Promise<void>;
  onResetAllAnswers: () => void;
  onUpdateL1: (emotion: EmotionWheelEntity | null) => void;
  onUpdateL2: (emotion: ComponentEmotionWheelLevels | null) => void;
  onUpdateSlices: (slices: EmotionWheelEntity[] | ComponentEmotionWheelLevels[], L1: EmotionWheelEntity | null) => void;
}

export const useEmotionWheelStore = create<EmotionWheelStore>()((set, get) => ({
  L1: null,
  L2: null,
  emotions: [],
  slices: [],
  emotionWheelpage: null,
  loading: false,
  onInit: async () => {
    set({ loading: true });
    const [emotions, page] = await Promise.all([getEmotionWheel(), getPage()]);
    set({ emotions: emotions.emotions, emotionWheelpage: page.page, loading: false });
  },
  onResetAllAnswers: () => {
    const { onUpdateSlices, emotions } = get();
    set({ L1: null, L2: null });
    onUpdateSlices(emotions, null);
  },
  onUpdateL1: (emotion: EmotionWheelEntity | null) => {
    set({
      L1: emotion,
    });
  },
  onUpdateL2: async (emotion: ComponentEmotionWheelLevels | null) => {
    const { L1 } = get();

    const { error } = await completeTask({
      entity: {
        currentEmotion: emotion,
        activeEmotionWheel: L1,
        activeEmotionWheelL2: emotion,
      },
      type: Enum_Completedtask_Type.EmotionalWheel,
    });

    set({
      L2: emotion,
    });

    if (error) {
      return;
    }
  },
  onUpdateSlices: (slices: EmotionWheelEntity[] | ComponentEmotionWheelLevels[], L1: EmotionWheelEntity | null) => {
    const gradient1From = L1?.attributes?.typeSection.mainGradientFrom;
    const gradient1To = L1?.attributes?.typeSection.mainGradientTo;
    const gradient2From = L1?.attributes?.typeSection.secondaryGradientFrom;
    const gradient2To = L1?.attributes?.typeSection.secondaryGradientTo;

    if (slices.length > 0) {
      set({
        slices: slices.map((emotion, index) => {
          if ('attributes' in emotion) {
            return {
              id: `gradient${emotion.attributes?.emotion}`,
              emotion: emotion.attributes?.emotion,
              start: '#' + emotion.attributes?.typeSection.mainGradientFrom,
              end: '#' + emotion.attributes?.typeSection.mainGradientTo,
            };
          }

          if ('emotion' in emotion) {
            return {
              id: `gradient${emotion?.emotion}`,
              emotion: emotion?.emotion,
              start: '#' + (index % 2 === 0 ? gradient1From : gradient2From),
              end: '#' + (index % 2 === 0 ? gradient1To : gradient2To),
            };
          }

          return {
            id: '',
            emotion: '',
            start: '',
            end: '',
          };
        }),
      });
    }
  },
}));
